import BackButton from 'components/backButton'
import { PurchaseTimerUpdateForm } from 'components/purchaseTimer'
import Modal from 'components/modal'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import {
  fetchPurchaseTimerById,
  deletePurchaseTimer,
} from 'utils/api/purchaseTimerApi'
import { useEffect, useState } from 'react'
import { PAGE_NOT_FOUND, PAGE_PURCHASE_TIMER } from 'utils/constants'
import { Button, Spinner } from 'components/common'
import { useSafeBack } from 'hooks'
import toast from 'react-hot-toast'

const PurchaseTimerUpdatePage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [purchaseTimer, setPurchaseTimer] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleBack = useSafeBack(PAGE_PURCHASE_TIMER.path)

  const getPurchaseTimer = async (id: string) => {
    try {
      const { data } = await fetchPurchaseTimerById(id)
      setPurchaseTimer({ ...data })
    } catch {
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      getPurchaseTimer(id)
    }
  }, [])

  const handleDelete = async () => {
    if (!id) return

    setDeleteLoading(true)
    const toastId = toast.loading('Deleting Purchase Timer...')
    try {
      await deletePurchaseTimer(id)
      toast.success('Purchase Timer deleted successfully.', { id: toastId })
      navigate(PAGE_PURCHASE_TIMER.path)
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.msg
      toast.error(msg || 'Failed to delete Purchase Timer.', { id: toastId })
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Update Purchase Timer</title>
      </Helmet>

      <BackButton handleBack={handleBack} />

      {purchaseTimer && (
        <>
          <div className="flex items-center justify-between">
            <h1 className="py-3 text-lg font-bold">
              {purchaseTimer.barcode} — {purchaseTimer.store}
            </h1>
            <Button
              type="button"
              variant="custom"
              buttonText="Delete"
              onClick={() => setShowDeleteModal(true)}
              className="rounded-md border-2 border-error bg-white px-2 py-1.5 text-sm font-bold capitalize text-error transition-colors hover:bg-error hover:text-white"
            />
          </div>

          <PurchaseTimerUpdateForm purchaseTimer={purchaseTimer} />
        </>
      )}

      {showDeleteModal && (
        <Modal
          width={500}
          title="Delete Purchase Timer"
          isOpen
          closeModal={() => {
            if (!deleteLoading) setShowDeleteModal(false)
          }}
        >
          <>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete the purchase timer for{' '}
              <strong>
                {purchaseTimer.barcode} — {purchaseTimer.store}
              </strong>
              ? This action cannot be undone.
            </p>
            <div className="mt-6 flex gap-4">
              <Button
                buttonText="Delete"
                className="w-full"
                type="button"
                loading={deleteLoading}
                onClick={handleDelete}
              />
              <Button
                buttonText="Cancel"
                variant="outline"
                type="button"
                className="w-full"
                disabled={deleteLoading}
                onClick={() => setShowDeleteModal(false)}
              />
            </div>
          </>
        </Modal>
      )}
    </>
  )
}

export default PurchaseTimerUpdatePage
